import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Upload, PenLine } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const KINDS = [
  { key: "docente", label: "Docente" },
  { key: "tutor", label: "Tutor" },
  { key: "soggetto_formatore", label: "Soggetto formatore" },
];

export default function SignaturesPage() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("draw"); // draw | upload
  const [form, setForm] = useState({ name: "", kind: "docente" });
  const [dataUrl, setDataUrl] = useState(null);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/signatures").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error(lang === "it" ? "Nome richiesto" : "Name required"); return; }
    try {
      if (mode === "draw") {
        if (!dataUrl) { toast.error(lang === "it" ? "Traccia la firma" : "Draw the signature"); return; }
        await api.post("/signatures/from-data-url", { ...form, data_url: dataUrl });
      } else {
        if (!file) { toast.error(lang === "it" ? "Seleziona un file" : "Select a file"); return; }
        const fd = new FormData();
        fd.append("name", form.name); fd.append("kind", form.kind); fd.append("file", file);
        await api.post("/signatures", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(t("save"));
      setOpen(false);
      setForm({ name: "", kind: "docente" });
      setDataUrl(null); setFile(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("delete") + "?")) return;
    await api.delete(`/signatures/${id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{lang === "it" ? "Firme grafiche" : "Signatures"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{lang === "it" ? "Libreria firme per attestati: docente, tutor, soggetto formatore." : "Signature library for certificates."}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full btn-lift" data-testid="create-signature-button">
              <Plus className="w-4 h-4 mr-1.5" /> {lang === "it" ? "Nuova firma" : "New signature"}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>{lang === "it" ? "Nuova firma" : "New signature"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{lang === "it" ? "Nome" : "Name"}</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dott. Bianchi" data-testid="sig-name-input" />
                </div>
                <div>
                  <Label>{lang === "it" ? "Tipo" : "Kind"}</Label>
                  <select
                    className="w-full h-9 border border-input rounded-md px-3 text-sm bg-background"
                    value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                    data-testid="sig-kind-select"
                  >
                    {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex gap-1 border-b border-border">
                <button type="button" onClick={() => setMode("draw")} className={`px-3 py-2 text-xs uppercase tracking-widest ${mode === "draw" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`} data-testid="sig-mode-draw">
                  <PenLine className="w-3.5 h-3.5 inline mr-1.5" /> {lang === "it" ? "Traccia" : "Draw"}
                </button>
                <button type="button" onClick={() => setMode("upload")} className={`px-3 py-2 text-xs uppercase tracking-widest ${mode === "upload" ? "text-primary border-b-2 border-primary" : "text-muted-foreground"}`} data-testid="sig-mode-upload">
                  <Upload className="w-3.5 h-3.5 inline mr-1.5" /> {lang === "it" ? "Carica immagine" : "Upload"}
                </button>
              </div>

              {mode === "draw" ? (
                <SignaturePad width={520} height={160} onChange={setDataUrl} />
              ) : (
                <div>
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full btn-lift">
                    <Upload className="w-4 h-4 mr-1.5" /> {lang === "it" ? "Scegli file" : "Choose file"}
                  </Button>
                  <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => setFile(e.target.files?.[0])} />
                  {file && <div className="text-xs mt-2 text-muted-foreground">{file.name}</div>}
                </div>
              )}

              <DialogFooter>
                <Button type="submit" className="rounded-full btn-lift" data-testid="sig-submit-button">{t("save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {KINDS.map((k) => {
          const items = list.filter((s) => s.kind === k.key);
          return (
            <div key={k.key} className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
                <div className="text-lg font-bold">{items.length}</div>
              </div>
              <ul className="divide-y divide-border">
                {items.map((s) => (
                  <li key={s.id} className="p-4 flex items-center gap-3">
                    <img src={`${BACKEND}${s.image_url}`} alt={s.name} className="h-10 w-24 object-contain bg-white border border-border rounded" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{s.name}</div>
                    </div>
                    <button onClick={() => remove(s.id)} data-testid={`delete-sig-${s.id}`} className="text-muted-foreground hover:text-destructive btn-lift">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
                {items.length === 0 && <li className="p-4 text-sm text-muted-foreground">{t("no_data")}</li>}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
